import '@shared/ui/theme/global.css';

import { initI18n } from '@shared/ui/i18n/i18n.config';
import { useEvictCacheOnUserChange } from '@shared/ui/query/evict-on-user-change';
import { QueryProvider } from '@shared/ui/query/query-provider.component';
import { Spinner } from '@shared/ui/spinner/spinner.component';
// Resolve to the same hashed asset the CSS @font-face points at, so the preload
// hits the real URL and the primary UI face doesn't wait on the CSS to parse.
import jakarta from '@shared/ui/theme/fonts/plus-jakarta-sans.woff2?url';
import { prePaintScript } from '@shared/ui/theme/theme';
import { ToastProvider } from '@shared/ui/toast/toast.component';
import type { ReactNode } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import en from './locales/en/common.json';
import { QUERY_CACHE_KEY } from './modules/core/cache';
import { useMe } from './modules/core/session.hooks';

export { ErrorBoundary } from '@shared/ui/error/error-boundary.component';

// Bundle en so the first paint has copy (no key-flash before i18n loads).
initI18n({ en: { common: en } });

// Client-side rendered: no server loader. The theme is applied before first paint
// by prePaintScript (reads the shared .kiss.cat cookie), so there's no flash.
// Brand (kiss.cat violet + pink accent) is defined per-theme in themes.css —
// shared with the Tasks app so sign-in reads as the same product.

export function Layout({ children }: { children: ReactNode }) {
	// data-theme is set before first paint by prePaintScript (below), which reads the
	// shared .kiss.cat cookie / localStorage. suppressHydrationWarning: the script
	// refines the attribute before React hydrates, so the build-time default differs.
	return (
		<html lang="en" data-theme="light" suppressHydrationWarning>
			<head>
				<meta charSet="utf-8" />
				<meta name="app-version" content={__APP_VERSION__} />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				{/* No-referrer app-wide: keeps secrets that ride in a URL (e.g. the invite
				    token in /invite/:token) out of the Referer header on any outbound
				    request. Replaces the per-route `headers` export, which SPA mode can't use. */}
				<meta name="referrer" content="no-referrer" />
				<link rel="icon" href="/favicon.svg" type="image/svg+xml" />
				{/* Preload the variable UI face (Plus Jakarta Sans)
				    so LCP text is not blocked on the stylesheet loading first. */}
				<link rel="preload" as="font" type="font/woff2" href={jakarta} crossOrigin="" />
				{/* Resolve the theme before first paint to avoid a flash. */}
				<script dangerouslySetInnerHTML={{ __html: prePaintScript }} />
				<Meta />
				<Links />
			</head>
			<body>
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

export default function App() {
	return (
		<QueryProvider
			persistKey={QUERY_CACHE_KEY}
			buster={__BUILD_ID__}
			onError={(error) => console.error(error)}
		>
			<ToastProvider>
				<SessionCacheGuard />
				<Outlet />
			</ToastProvider>
		</QueryProvider>
	);
}

/** Drop a stale persisted cache when a different account becomes active (add-account /
 *  switch). Renders nothing; lives inside the QueryProvider to reach the query client. */
function SessionCacheGuard() {
	const { data: me } = useMe();
	useEvictCacheOnUserChange(me?.user.id, QUERY_CACHE_KEY);
	return null;
}

// Shown while the client bundle boots and hydrates (SPA mode prerenders this into
// index.html). A centred spinner on the themed background — brief, first load only.
export function HydrateFallback() {
	return (
		<div
			style={{
				minHeight: '100dvh',
				display: 'grid',
				placeItems: 'center',
				background: 'var(--kc-bg, #09080f)',
			}}
		>
			<Spinner size="large" />
		</div>
	);
}
